// biome-ignore lint/style/noRestrictedImports: typings
import type { TFunction } from 'i18next';
import every from 'lodash-es/every';
import { getLogger } from 'loglevel';
import { z } from 'zod';

import { getPropertyNameCaseInsensitive } from '@apple/utils/object';

import type { ValidationRule } from './models';

const log = getLogger('validation');

const zType = (s: unknown): string | undefined => (s as { _def?: { type?: string } })?._def?.type;
type CustomError = { error: string };

export type extendSchemaWithServerRulesProps<T extends z.ZodObject<z.ZodRawShape>> = {
	existingSchema: T;
	rules: ValidationRule[];
	whitelist: string[];
	t: TFunction<'zod', undefined>;
	ignoreLocalization?: boolean;
};

export function extendSchemaWithServerRules<T extends z.ZodObject<z.ZodRawShape>>({
	existingSchema,
	rules,
	whitelist,
	t,
	ignoreLocalization = false,
}: extendSchemaWithServerRulesProps<T>): T {
	const schemaExtensions: Record<string, z.ZodType> = {};

	rules.forEach(rule => {
		const fieldName = getPropertyNameCaseInsensitive(existingSchema.shape, rule.fieldName);
		if (!fieldName) {
			const message = `Field ${rule.fieldName} not found in schema`;
			log.error(message);
			throw new Error(message);
		}

		const fieldSchema =
			schemaExtensions[fieldName] ??
			(existingSchema.shape as Record<string, z.ZodType>)[fieldName];
		if (!fieldSchema) {
			throw new Error(`Field ${fieldName} not found in schema`);
		}

		schemaExtensions[fieldName] = extendSchemaWithServerRule(
			fieldSchema,
			fieldName,
			rule,
			whitelist,
			t,
			ignoreLocalization,
		);
	});

	// Merge the existing schema with the new extensions
	return existingSchema.extend(schemaExtensions) as unknown as T;
}

function extendSchemaWithServerRule(
	fieldSchema: z.ZodType,
	fieldName: string,
	rule: ValidationRule,
	whitelist: string[],
	t: TFunction<'zod', undefined>,
	ignoreLocalization = false,
): z.ZodType {
	log.debug('Extending field with server validation rule:', fieldName, rule);

	//TODO: Implement group rules?
	//TODO: Implement visibility rule?
	const customMessage =
		rule.validationMessage !== null && rule.validationMessage !== undefined
			? {
					error: ignoreLocalization
						? rule.validationMessage
						: t(`errors.${rule.validationMessage}`),
				}
			: undefined;

	fieldSchema = ensureUnwrapped(fieldSchema, unwrappedSchema =>
		applyRequiredRule(unwrappedSchema, rule, fieldName, customMessage),
	);
	fieldSchema = ensureUnwrapped(fieldSchema, unwrappedSchema =>
		applyMinValueRule(unwrappedSchema, rule, fieldName, customMessage),
	);
	fieldSchema = ensureUnwrapped(fieldSchema, unwrappedSchema =>
		applyMaxValueRule(unwrappedSchema, rule, fieldName, customMessage),
	);
	fieldSchema = ensureUnwrapped(fieldSchema, unwrappedSchema =>
		applyMinLengthRule(unwrappedSchema, rule, fieldName, customMessage),
	);
	fieldSchema = ensureUnwrapped(fieldSchema, unwrappedSchema =>
		applyMaxLengthRule(unwrappedSchema, rule, fieldName, customMessage),
	);
	fieldSchema = ensureUnwrapped(fieldSchema, unwrappedSchema =>
		applyRegexRule(unwrappedSchema, rule, fieldName, customMessage),
	);
	fieldSchema = ensureUnwrapped(fieldSchema, unwrappedSchema =>
		applyAcceptedValuesRule(unwrappedSchema, rule, fieldName, customMessage),
	);
	if ((rule.acceptedValues?.length ?? 0) === 0) {
		fieldSchema = ensureUnwrapped(fieldSchema, unwrappedSchema =>
			applyWhitelistRule(unwrappedSchema, rule, fieldName, whitelist, customMessage),
		);
	}

	return fieldSchema;
}

function applyRequiredRule(
	fieldSchema: z.ZodType,
	rule: ValidationRule,
	fieldName: string,
	_customError: CustomError | undefined,
): z.ZodType {
	if (
		rule.isRequired &&
		(fieldSchema.def.type === 'array' || fieldSchema.def.type === 'string')
	) {
		log.debug(`${fieldName}: applying required validation rule to field`);

		if (zType(fieldSchema) === 'string') {
			return (fieldSchema as z.ZodString).min(1);
		}

		// array
		return (fieldSchema as z.ZodArray<z.ZodType>).min(1);
	}

	if (!rule.isRequired) {
		log.debug(`    ${fieldName}: applying optional validation rule to field`);
		fieldSchema = fieldSchema.optional();
		return fieldSchema;
	}

	return fieldSchema;
}

function applyMinValueRule(
	fieldSchema: z.ZodType,
	rule: ValidationRule,
	fieldName: string,
	customError: CustomError | undefined,
): z.ZodType {
	if (rule.minValue == undefined) {
		return fieldSchema;
	}

	if (fieldSchema.def.type === 'number') {
		log.debug(
			`    ${fieldName}: applying minValue '${rule.minValue}' validation rule to number field`,
		);
		fieldSchema = (fieldSchema as z.ZodNumber).min(rule.minValue, customError);

		return fieldSchema;
	}

	log.warn(
		`    ${fieldName}: ignoring minimum value validation rule for field of type ${zType(fieldSchema)}. Minimum value is only supported for number fields.`,
	);
	return fieldSchema;
}

function applyMaxValueRule(
	fieldSchema: z.ZodType,
	rule: ValidationRule,
	fieldName: string,
	customError: CustomError | undefined,
): z.ZodType {
	if (rule.maxValue == undefined) {
		return fieldSchema;
	}

	if (fieldSchema.def.type === 'number') {
		log.debug(
			`    ${fieldName}: applying maxValue '${rule.maxValue}' validation rule to number field`,
		);
		fieldSchema = (fieldSchema as z.ZodNumber).max(rule.maxValue, customError);

		return fieldSchema;
	}

	log.warn(
		`    ${fieldName}: ignoring maximum value validation rule for field of type ${zType(fieldSchema)}. Maximum value is only supported for number fields.`,
	);
	return fieldSchema;
}

function applyMinLengthRule(
	fieldSchema: z.ZodType,
	rule: ValidationRule,
	fieldName: string,
	customError: CustomError | undefined,
): z.ZodType {
	if (rule.minLength == undefined) {
		return fieldSchema;
	}

	if (fieldSchema.def.type === 'string') {
		log.debug(
			`    ${fieldName}: applying minLength '${rule.minLength}' validation rule to string field`,
		);
		return (fieldSchema as z.ZodString).min(rule.minLength, customError);
	}

	if (fieldSchema.def.type === 'array') {
		log.debug(
			`    ${fieldName}: applying minLength '${rule.minLength}' validation rule to array field`,
		);
		return (fieldSchema as z.ZodArray<z.ZodType>).min(rule.minLength, customError);
	}

	log.warn(
		`    ${fieldName}: ignoring minimum length validation rule for field of type ${zType(fieldSchema)}. Minimum length is only supported for string and array fields.`,
	);
	return fieldSchema;
}

function applyMaxLengthRule(
	fieldSchema: z.ZodType,
	rule: ValidationRule,
	fieldName: string,
	customError: CustomError | undefined,
): z.ZodType {
	if (rule.maxLength == undefined) {
		return fieldSchema;
	}

	if (fieldSchema.def.type === 'string') {
		log.debug(
			`    ${fieldName}: applying maxLength '${rule.maxLength}' validation rule to string field`,
		);
		return (fieldSchema as z.ZodString).max(rule.maxLength, customError);
	}

	if (fieldSchema.def.type === 'array') {
		log.debug(
			`    ${fieldName}: applying maxLength '${rule.maxLength}' validation rule to array field`,
		);
		return (fieldSchema as z.ZodArray<z.ZodType>).max(rule.maxLength, customError);
	}

	log.warn(
		`    ${fieldName}: ignoring maximum length validation rule for field of type ${zType(fieldSchema)}. Maximum length is only supported for string and array fields.`,
	);
	return fieldSchema;
}

function applyRegexRule(
	fieldSchema: z.ZodType,
	rule: ValidationRule,
	fieldName: string,
	customError: CustomError | undefined,
): z.ZodType {
	if (rule.regex == undefined) {
		return fieldSchema;
	}

	if (fieldSchema.def.type === 'string') {
		log.debug(
			`    ${fieldName}: applying regex '${rule.regex}' validation rule to string field`,
		);
		return (fieldSchema as z.ZodString).regex(new RegExp(rule.regex), customError);
	}

	log.warn(
		`    ${fieldName}: ignoring regex validation rule for field of type ${zType(fieldSchema)}. Regex is only supported for string fields.`,
	);
	return fieldSchema;
}

function applyAcceptedValuesRule(
	fieldSchema: z.ZodType,
	rule: ValidationRule,
	fieldName: string,
	customError: CustomError | undefined,
): z.ZodType {
	if (rule.acceptedValues == undefined || rule.acceptedValues.length <= 0) {
		return fieldSchema;
	}

	if (fieldSchema.def.type === 'array' || fieldSchema.def.type === 'string') {
		const acceptedValues = new Set(rule.acceptedValues.map(av => av.key));
		log.debug(
			`    ${fieldName}: applying acceptedValues '${[...acceptedValues].join(', ')}' validation rule to string field`,
		);

		return fieldSchema.def.type === 'array'
			? (fieldSchema as z.ZodArray<z.ZodString>).refine(
					(val: string[]) => val.every(v => acceptedValues.has(String(v))),
					customError,
				)
			: (fieldSchema as z.ZodString).refine(val => acceptedValues.has(val), customError);
	}

	log.warn(
		`    ${fieldName}: ignoring accepted values validation rule for field of type ${zType(fieldSchema)}. Accepted values is only supported for string and string[] fields.`,
	);
	return fieldSchema;
}

function applyWhitelistRule(
	fieldSchema: z.ZodType,
	rule: ValidationRule,
	fieldName: string,
	whitelist: string[],
	customError: CustomError | undefined,
): z.ZodType {
	if (!rule.checkWhitelist) {
		return fieldSchema;
	}

	if (fieldSchema.def.type === 'string') {
		return (fieldSchema as z.ZodString).refine(
			(val: string) => containsOnlyWhitelistedCharacters(val, whitelist),
			customError,
		);
	}

	if (fieldSchema.def.type === 'enum') {
		log.debug(`${fieldName}: ignoring whitelist check.`);
		return fieldSchema;
	}

	log.warn(
		`    ${fieldName}: ignoring whitelist validation rule for field of type ${zType(fieldSchema)}. Whitelist is only supported for string fields.`,
	);
	return fieldSchema;
}

function containsOnlyWhitelistedCharacters(value: string, whitelist: string[]) {
	if (!value || !whitelist || whitelist.length === 0) {
		return true;
	}

	return every(value, char => whitelist.includes(char.toLowerCase()));
}

type ModifierFunction<T extends z.ZodType> = (schema: T) => T;

function ensureUnwrapped<T extends z.ZodType>(schema: T, modifier: ModifierFunction<T>): z.ZodType {
	let wasOptional = false;
	let wasNullable = false;
	let wasPromise = false;

	let modifiedSchema: z.ZodType = schema;
	while (
		modifiedSchema?.def?.type === 'optional' ||
		modifiedSchema?.def?.type === 'nullable' ||
		modifiedSchema?.def?.type === 'promise' ||
		zType(modifiedSchema) === 'brand'
	) {
		if (modifiedSchema?.def?.type === 'optional') {
			wasOptional = true;
			modifiedSchema = (modifiedSchema as z.ZodOptional<z.ZodType>).unwrap();
		}
		if (modifiedSchema?.def?.type === 'nullable') {
			wasNullable = true;
			modifiedSchema = (modifiedSchema as z.ZodNullable<z.ZodType>).unwrap();
		}
		if (modifiedSchema?.def?.type === 'promise') {
			wasPromise = true;
			modifiedSchema = (modifiedSchema as z.ZodPromise<z.ZodType>).unwrap();
		}
		if (zType(modifiedSchema) === 'brand') {
			throw new Error('Modifying branded schemas is not supported yet');
		}
	}

	modifiedSchema = modifier(modifiedSchema as T);

	if (wasPromise) {
		modifiedSchema = z.promise(modifiedSchema as z.ZodType);
	}
	if (wasNullable) {
		modifiedSchema = z.nullable(modifiedSchema as z.ZodType);
	}
	if (wasOptional) {
		modifiedSchema = z.optional(modifiedSchema as z.ZodType);
	}

	return modifiedSchema;
}
